import { useState } from 'react';
import { ActionIcon, Button, Group, Menu, Modal, Text } from '@mantine/core';
import { IconDotsVertical, IconUserMinus } from '@tabler/icons-react';
import { useParticipantInfo, useRoomContext } from '@livekit/components-react';
import type { Participant } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import { identityGuestId } from '../../../server/participant-identity.ts';
import { useLocalEmployeeId } from '../cloud-recording';
import { useMeetingFeatures } from '../meeting-features';
import { participantName } from './participant-name';
import { notifyError } from '@/shared/notify';

async function removeGuest(roomName: string, identity: string) {
    const params = new URLSearchParams({ roomName, identity });
    const response = await fetch(`/api/moderation/remove?${params.toString()}`, { method: 'POST' });
    if (!response.ok) throw new Error(`${String(response.status)} ${await response.text()}`);
}

export function RemoveGuestMenu({ participant, className }: Readonly<{ participant: Participant; className?: string }>) {
    const { t } = useTranslation('conference');
    const room = useRoomContext();
    const isEmployee = useLocalEmployeeId() !== null;
    const { guests } = useMeetingFeatures();
    const { name } = useParticipantInfo({ participant });
    const { identity } = participant;
    const [confirmOpened, setConfirmOpened] = useState(false);

    if (!isEmployee || !guests || identityGuestId(identity) === null) return null;

    const close = () => {
        setConfirmOpened(false);
    };

    return (
        <>
            <Menu withinPortal position="bottom-end">
                <Menu.Target>
                    <ActionIcon
                        aria-label={t('removal.actions')}
                        className={className}
                        data-testid="participant-actions"
                        radius="var(--radius-rounded-full)"
                        size="sm"
                        variant="subtle"
                    >
                        <IconDotsVertical size={16} />
                    </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        color="danger"
                        data-testid="remove-guest"
                        leftSection={<IconUserMinus size={16} />}
                        onClick={() => {
                            setConfirmOpened(true);
                        }}
                    >
                        {t('removal.remove')}
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
            <Modal centered opened={confirmOpened} title={t('removal.confirm.title')} onClose={close}>
                <Text size="sm">{t('removal.confirm.message', { name: participantName(name, identity) })}</Text>
                <Group gap="var(--spacing-2)" justify="flex-end" mt="var(--spacing-5)">
                    <Button variant="default" onClick={close}>
                        {t('removal.confirm.cancel')}
                    </Button>
                    <Button
                        color="danger"
                        data-testid="confirm-remove-guest"
                        onClick={() => {
                            close();
                            removeGuest(room.name, identity).catch((error: unknown) => {
                                notifyError(t('removal.error'), error);
                            });
                        }}
                    >
                        {t('removal.confirm.confirm')}
                    </Button>
                </Group>
            </Modal>
        </>
    );
}
